import { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { createSelector } from "reselect"
import { ColumnDef } from "@tanstack/react-table"
import HrReportTemplate from "Components/templates/hr/hr-report"
import SearchSelect from "Components/molecule/inputs/search-select"
import BarChartCard from "Components/organisms/hr-reports/bar-chart-card"
import EmployeeLink from "Components/organisms/hr-reports/employee-link"
import EmployeeTable from "Components/organisms/hr-reports/employee-table"
import FilterBar from "Components/organisms/hr-reports/filter-bar"
import StatCard from "Components/organisms/hr-reports/stat-card"
import useCrossFilter, { CrossFilterDimension } from "hooks/useCrossFilter"
import { HrRetentionRow, NOT_SET } from "models/hr-reports"
import { HrReportsSliceState, setRetentionReport } from "slices/human-resources/hr-reports/reducer"
import { getRetentionReport } from "slices/human-resources/hr-reports/thunk"
import FiscalCalendar, { DEFAULT_FISCAL_START } from "utils/fiscal-calendar"
import { formatDate, groupByDepartment, HR_DIM, HR_TOP_FILTERS, hrReportDimensions, MONTHS } from "utils/hr-reports"

const selectHrReports = createSelector(
    (state: any) => state.HrReports,
    ({ retention }: HrReportsSliceState) => ({ retention })
)

const YEARS_OF_SERVICE = "yearsOfService"
const REASON = "reason"
const MONTH = "month"
const MAX_YEAR_BUCKET = 10

// "< 1 year", "1 year" ... "10+ years"
const serviceBucket = (years: number | null) => {
    if (years === null) return NOT_SET
    if (years < 1) return "< 1 year"
    if (years >= MAX_YEAR_BUCKET) return `${MAX_YEAR_BUCKET}+ years`
    return years === 1 ? "1 year" : `${years} years`
}
const bucketOrder = (label: string) => (label === NOT_SET ? 999 : label.startsWith("<") ? 0 : parseInt(label))

// Months in fiscal order (September first)
const FISCAL_MONTHS = MONTHS.map((_, i) => MONTHS[(i + DEFAULT_FISCAL_START - 1) % 12])
const terminationMonth = (r: HrRetentionRow) => MONTHS[new Date(r.terminatedAt).getUTCMonth()]

// Charts in display order (Terminations by Department / by Month sit above them)
const CHARTS = [
    { key: REASON, title: "Reason of Termination" },
    { key: YEARS_OF_SERVICE, title: "Years of Service at Exit", order: "label" as const },
    { key: HR_DIM.positionType, title: "Position Type" },
    { key: HR_DIM.officeLocation, title: "Office Location" },
    { key: HR_DIM.gender, title: "Gender" },
    { key: HR_DIM.indigenous, title: "Indigenous" },
    { key: HR_DIM.designation, title: "Designation" },
    { key: HR_DIM.identity, title: "Identity" },
]

const text = (value: string | null) => value ?? "-"

const COLUMNS: ColumnDef<HrRetentionRow, any>[] = [
    { accessorKey: "name", header: "Employee", size: 200, cell: ({ row }) => <EmployeeLink id={row.original.id} name={row.original.name} /> },
    { accessorKey: "division", header: "Division", size: 180, cell: ({ getValue }) => text(getValue()) },
    { accessorKey: "department", header: "Department", size: 200, cell: ({ getValue }) => text(getValue()) },
    { accessorKey: "officeLocation", header: "Office Location", size: 170, cell: ({ getValue }) => text(getValue()) },
    { accessorKey: "manager", header: "Manager", size: 180, cell: ({ row }) => <EmployeeLink id={row.original.managerId} name={row.original.manager} /> },
    { accessorKey: "terminatedAt", header: "Termination Date", size: 150, cell: ({ getValue }) => formatDate(getValue()) },
    { accessorKey: "yearsOfService", header: "Years of Service", size: 130, cell: ({ getValue }) => getValue() ?? "-" },
    { accessorKey: "reason", header: "Reason of Termination", size: 260, cell: ({ getValue }) => text(getValue()) },
]

const searchText = (r: HrRetentionRow) => [r.name, r.division, r.department, r.officeLocation, r.manager, r.reason].join(" ")

const RetentionPage = () => {
    document.title = "Retention | IbexIQ"
    const dispatch: any = useDispatch()
    const { retention } = useSelector(selectHrReports)
    const [fiscalYear, setFiscalYear] = useState(retention?.fiscalYear ?? FiscalCalendar.currentFiscalYear())
    // The fiscal year field applies on Search, like the other filters
    const [draftFiscalYear, setDraftFiscalYear] = useState(fiscalYear)
    const [isLoading, setIsLoading] = useState(true)
    const [hasError, setHasError] = useState(false)

    const load = async (fy: number) => {
        setIsLoading(true)
        setHasError(false)
        const report = await getRetentionReport(fy)
        if (report) dispatch(setRetentionReport(report))
        else setHasError(true)
        setIsLoading(false)
    }

    useEffect(() => { load(fiscalYear) }, [fiscalYear])

    // Show cached rows only while they belong to the fiscal year being shown
    const report = retention?.fiscalYear === fiscalYear ? retention : null

    const dimensions = useMemo<CrossFilterDimension<HrRetentionRow>[]>(() => [
        ...hrReportDimensions<HrRetentionRow>(),
        { key: YEARS_OF_SERVICE, label: "Years of Service", values: r => [serviceBucket(r.yearsOfService)] },
        { key: REASON, label: "Reason", values: r => [r.reason?.trim() || NOT_SET] },
        { key: MONTH, label: "Month", values: r => [terminationMonth(r)] },
    ], [])
    const filter = useCrossFilter<HrRetentionRow>(report?.rows ?? [], dimensions)
    const { rowsExcept } = filter

    const departments = useMemo(() => groupByDepartment(rowsExcept(HR_DIM.department))
        .map(d => ({ value: d.department, count: d.people.length })), [rowsExcept])

    const monthCounts = useMemo(() => {
        const counts = filter.countsFor(MONTH)
        return FISCAL_MONTHS.map(m => ({ value: m, count: counts.find(c => c.value === m)?.count ?? 0 }))
    }, [filter])

    const fiscalYearOptions = (retention?.fiscalYears.length ? retention.fiscalYears : [fiscalYear])
        .map(fy => ({ value: String(fy), label: `FY${fy}` }))

    const countsFor = (key: string) => {
        const counts = filter.countsFor(key)
        return key === YEARS_OF_SERVICE ? counts.sort((a, b) => bucketOrder(a.value) - bucketOrder(b.value)) : counts
    }

    // Stat cards
    const rows = filter.filtered
    const withYears = rows.filter(r => r.yearsOfService !== null)
    const averageYears = withYears.length ? withYears.reduce((sum, r) => sum + r.yearsOfService!, 0) / withYears.length : null
    const reasonRows = rowsExcept(REASON)
    const topReason = filter.countsFor(REASON)
        .filter(c => c.value !== NOT_SET && c.count > 0)
        .sort((a, b) => b.count - a.count)[0]
    const earlyExits = filter.countsFor(YEARS_OF_SERVICE).find(c => c.value === "< 1 year")
    const selectedReason = filter.selectedFor(REASON)
    const selectedService = filter.selectedFor(YEARS_OF_SERVICE)

    const togglePick = (key: string, value: string, selected: string[]) =>
        filter.select(key, selected.length === 1 && selected[0] === value ? [] : [value])

    return <HrReportTemplate
        title="Retention"
        manual="human-resources/retention"
        isLoading={isLoading && report === null}
        hasError={hasError && report === null}
        onRetry={() => load(fiscalYear)}
    >
        <FilterBar
            filter={filter}
            dropdowns={HR_TOP_FILTERS}
            onSearch={() => setFiscalYear(draftFiscalYear)}
            onClear={() => {
                const current = FiscalCalendar.currentFiscalYear()
                setDraftFiscalYear(current)
                setFiscalYear(current)
            }}
        >
            <SearchSelect
                label="Fiscal Year"
                value={String(draftFiscalYear)}
                onChange={(o) => o?.value && setDraftFiscalYear(Number(o.value))}
                options={fiscalYearOptions}
            />
        </FilterBar>

        <div className="hr-report-stats">
            <StatCard
                label="Terminations"
                value={rows.length}
                caption={`FY${fiscalYear} (September ${fiscalYear - 1} to August ${fiscalYear})`}
            />
            <StatCard
                label="Average years of service"
                value={averageYears === null ? "-" : averageYears.toFixed(1)}
                caption="At exit"
            />
            <StatCard
                label="Left in the first year"
                value={earlyExits?.count ?? 0}
                caption="Less than 1 year of service"
                active={selectedService.length === 1 && selectedService[0] === "< 1 year"}
                onClick={() => togglePick(YEARS_OF_SERVICE, "< 1 year", selectedService)}
            />
            <StatCard
                label="Top reason"
                value={topReason ? <span className="fs-5">{topReason.value}</span> : "-"}
                caption={topReason ? `${topReason.count} of ${reasonRows.length} terminations` : undefined}
                active={!!topReason && selectedReason.length === 1 && selectedReason[0] === topReason.value}
                onClick={topReason ? () => togglePick(REASON, topReason.value, selectedReason) : undefined}
            />
        </div>

        <div className="hr-report-grid">
            <BarChartCard
                title="Terminations by Department"
                data={departments}
                unit="terminations"
                selected={filter.selectedFor(HR_DIM.department)}
                onSelect={(value, additive) => filter.toggle(HR_DIM.department, value, additive)}
                onClear={() => filter.clear(HR_DIM.department)}
            />
            <BarChartCard
                title="Terminations by Month"
                data={monthCounts}
                order="label"
                unit="terminations"
                selected={filter.selectedFor(MONTH)}
                onSelect={(value, additive) => filter.toggle(MONTH, value, additive)}
                onClear={() => filter.clear(MONTH)}
            />
            {CHARTS.map(c => (
                <BarChartCard
                    key={c.key}
                    title={c.title}
                    data={countsFor(c.key)}
                    order={c.order ?? "count"}
                    unit="terminations"
                    selected={filter.selectedFor(c.key)}
                    onSelect={(value, additive) => filter.toggle(c.key, value, additive)}
                    onClear={() => filter.clear(c.key)}
                />
            ))}
        </div>

        <EmployeeTable
            title="Terminated Employees"
            report="Retention"
            rows={rows}
            columns={COLUMNS}
            searchText={searchText}
            initialSorting={[{ id: "terminatedAt", desc: true }]}
        />
    </HrReportTemplate>
}

export default RetentionPage
